import { Box, Card, Grid } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { amplitude } from 'wisestep-api';
import { WsButton, WsColors, WsDrawer, WsTypography, WsTypoVariants } from 'wisestep-base';

import { useWorkspaceContext } from '../../../../../../src/context';
import { JobBoardsAction, useJobAccessControl } from '../../../../../hooks/useJobAccessControl';
import { warning } from '../../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../../utils/NotificationStore';
import { getWorkAssignments } from '../../../helper';
import { defaultWorkAssignments, IAssignCardProps, TeamsState } from '../../types';
import RecruitersTable from '../RecruitersTable';
import WorkspaceTeamsTable from '../workspaceTeamsTable';

const AssignCard = ({ name, title, icon, jobId, disableButton }: IAssignCardProps) => {
  const workspace = useWorkspaceContext();
  const [open, setOpen] = useState(false);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [refresh, setRefresh] = useState<boolean>(false);
  const { access: jobAccesControl } = useJobAccessControl([
    JobBoardsAction.USER_ASSIGNMENT,
    JobBoardsAction.TEAM_ASSIGNMENT,
  ]);

  const userAssignAccessForJob = workspace.owns || jobAccesControl[0];
  const teamAssignAccessForJob = workspace.owns || jobAccesControl[1];

  const handleDrawerOpen = () => {
    if (title === 'Teams' && !teamAssignAccessForJob) {
      setNotification(warning('You do not have enough permissions to perform this action'));
      return;
    } else if (title === 'Recruiters' && !userAssignAccessForJob) {
      setNotification(warning('You do not have enough permissions to perform this action'));
      return;
    } else {
      firingAmplitudeEvent[name]();
      setOpen(true);
    }
  };

  const reload = () => setOpen(false);
  const [teamsResponse, setTeamsResponse] = useState<TeamsState>();

  useEffect(() => {
    const getAssignments = async () => {
      const assignments = await getWorkAssignments(jobId);
      setTeamsResponse({ workAssignments: assignments });
    };
    getAssignments();
  }, [jobId, open, refresh]);

  const assignedText =
    name === 'teams'
      ? ` ${teamsResponse?.workAssignments?.teams?.length ?? 0} Assigned`
      : ` ${
          (teamsResponse?.workAssignments?.recruiters?.length ?? 0) +
          (teamsResponse?.workAssignments?.teamMembers?.length ?? 0)
        } Assigned`;

  const firingAmplitudeEvent = {
    ['recruiters']: () =>
      amplitude('Job _assignrecruiters', {
        clickedOn: 'Assign recruiters',
        pageName: 'jobs assign',
        pageUrl: window.location.href,
        workspaceName: workspace.name,
        planType: workspace.planType,
        interfaceType: 'Web',
      }),
    ['teams']: () =>
      amplitude('Job_assignteams', {
        clickedOn: 'Assign teams',
        pageName: 'jobs assign',
        pageUrl: window.location.href,
        workspaceName: workspace.name,
        planType: workspace.planType,
        interfaceType: 'Web',
      }),
  };

  return (
    <>
      <Card variant="outlined" square sx={{ borderRadius: 2, alignContent: 'center' }}>
        <Grid container p={4} spacing={2}>
          <Grid
            item
            bgcolor={WsColors.TertiaryShades50}
            xs={3}
            m={1}
            borderRadius={2}
            border="1px solid #EAEAF1"
          >
            {icon}
          </Grid>
          <Grid item xs={8} alignContent={'flex-start'}>
            <WsTypography variant={WsTypoVariants.SUB_TITLE}>{title}</WsTypography>
            {name != 'partners' && <WsButton variant="text">{`${assignedText}`}</WsButton>}
          </Grid>
          <Grid item xs={12}>
            <Box id={name}>
              <WsButton
                fullWidth
                onClick={handleDrawerOpen}
                variant="outlined"
                size="large"
                disabled={name === 'partners' || disableButton}
              >
                {name === 'partners' ? 'Coming Soon' : 'Assign'}
              </WsButton>
            </Box>
          </Grid>
        </Grid>
      </Card>
      {name === 'teams' && (
        <WsDrawer open={open} onClose={reload} title={'Teams'} headerSize="medium" size="small">
          <WorkspaceTeamsTable
            workAssignments={teamsResponse?.workAssignments ?? defaultWorkAssignments}
            jobId={jobId}
            refreshAssignments={() => setRefresh(!refresh)}
          />
        </WsDrawer>
      )}
      {name === 'recruiters' && (
        <WsDrawer
          open={open}
          onClose={reload}
          title={'Recruiters'}
          headerSize="medium"
          size="small"
        >
          <RecruitersTable
            workAssignments={teamsResponse?.workAssignments ?? defaultWorkAssignments}
            jobId={jobId}
          />
        </WsDrawer>
      )}
    </>
  );
};
export default AssignCard;
